/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ControllerPlaceholderBase", ["ControllerBase"], function (context, cls) {
  /**
   * Base class used for Auto Widgets controllers
   * @class ControllerPlaceholderBase
   * @memberOf classes
   * @extends classes.ControllerBase
   */
  class ControllerPlaceholderBase extends cls.ControllerBase /** @lends classes.ControllerPlaceholderBase.prototype */ {
    // _behaviorsApplyedByPlaceholderOnVirtualWidget (a Set) is intentionally NOT declared as a
    // class field: it is populated during super() construction (via _addBehavior called from the
    // subclass _initBehaviors), and a field initializer running after super() would clobber it.
    // It is lazily created in _addBehavior instead.

    /**
     * Reference to all the event handlers
     * in order to unbind them
     * @type {HandleRegistration[]}
     */
    _handlers = null;

    /**
     * Hold the reference to all the instanciated widgets
     * done by this controller.
     * The key is the application id,
     * or the window id,
     * or the action id
     * @type {Map<string, cls.WidgetBase>}
     */
    _autoWidgets = null;

    /**
     * This placeholder contains application
     * @type {boolean}
     */
    _isPrograms = false;
    /**
     * This placeholder contains windows
     * @type {boolean}
     */
    _isWindows = false;
    /**
     * This placeholder contains actions
     * @type {boolean}
     */
    _isActions = false;

    /**
     * Does this controller affect the layout
     * on auto widget creation/deletion
     * @type {boolean}
     */
    _affectLayout = false;

    /**
     * Factory to use when the widget is not in the ChromeBar
     */
    _autoWidgetType = "";

    constructor(bindings) {
      super(bindings);
      this._autoWidgets = new Map();
      this._autoWidgetType = "Label";
      this._handlers = [];
    }

    /**
     * @inheritDoc
     */
    destroy() {
      super.destroy();

      this._destroyAutoWidget();
      this._autoWidgets = null;

      for (const handler of this._handlers) {
        handler();
      }
      this._handlers.length = 0;
      this._handlers = null;
    }

    /**
     * @inheritDoc
     */
    attachUI() {
      //If we are a placeholder only children are added to the parent widget (ex:TopMenuGroup)
      super.attachUI();

      this._initAutoWidgets();
    }

    /**
     * detach UI
     */
    detachUI() {
      for (const autoWidgetKey of this._autoWidgets.keys()) {
        this._destroyAutoWidget(autoWidgetKey);
      }

      super.detachUI();
    }

    //#region  Handlers registration
    /**
     * Register application handlers
     * @protected
     */
    _registerProgramsHandlers() {
      this._isPrograms = true;
      const parentApplication = this.getAnchorNode().getApplication(),
        navigationManager = parentApplication.getSession().getNavigationManager();

      this._handlers.push(
        navigationManager.onApplicationAdded((event, invoker, application) => {
          if (application.isAwaited()) {
            return;
          }
          this._addApplication(application);
        })
      );

      this._handlers.push(
        navigationManager.onApplicationRemoved((event, invoker, application) => {
          this._destroyAutoWidget(application.applicationHash);
        })
      );
    }

    /**
     * Register window handlers
     * @protected
     */
    _registerWindowsHandlers() {
      this._isWindows = true;
      const parentApplication = this.getAnchorNode().getApplication(),
        navigationManager = parentApplication.getSession().getNavigationManager();

      this._handlers.push(
        // Listen only to the same application as the controller application
        navigationManager.when(context.constants.applicationEvents.windowAdded, (event, invoker, windowNode, rootApplication) => {
          //We can add element only if we are already attached
          const thisRootApplication = navigationManager.getRootWaitingApplication(parentApplication);
          // Only add windows of the same root application
          if (rootApplication !== thisRootApplication) {
            return;
          }
          this._addWindow(parentApplication, windowNode);
        })
      );

      this._handlers.push(
        navigationManager.when(context.constants.applicationEvents.windowRemoved, (event, invoker, windowNode, rootApplication) => {
          this._destroyAutoWidget(this._generateNodeIdentifier(windowNode));
        })
      );
    }

    /**
     * Initialise handlers to auto create actions
     * @private
     */
    _registerActionHandlers() {
      this._isActions = true;
      this._handlers.push(
        cls.ControllerFactory.onControllerCreated("MenuAction", (event, realWidgetController) => {
          if (realWidgetController.getWidget().getApplicationIdentifier() === this.getWidget().getApplicationIdentifier()) {
            this._addAction(realWidgetController);
          }
        })
      );
      this._handlers.push(
        cls.ControllerFactory.onControllerCreated("Action", (event, realWidgetController) => {
          if (realWidgetController.getWidget().getApplicationIdentifier() === this.getWidget().getApplicationIdentifier()) {
            this._addAction(realWidgetController);
          }
        })
      );
      // Listen to the navigation manager on current window change
      const parentApplicationUi = this.getAnchorNode().getApplication().getUI();
      this._handlers.push(
        parentApplicationUi.when(context.constants.applicationEvents.currentWindowChanged, (evt, invoker) => {
          // Clear and init auto items again
          this._destroyAutoWidgets();
          this._initAutoActions();
        })
      );
    }

    //#endregion

    //#region BEHAVIOR management
    /**
     * Return the common behaviours between this behavior and the one in parameters
     * Excepted those that are applied on virtual widgets by this placeholder controller
     * @param {classes.ControllerBase} controller Source to copy behavior from
     * @return {Set} Common behaviors that should be applied on virtual widget
     */
    _commonBehaviors(controller) {
      const res = new Set();
      const appliedByPlaceholder = this._behaviorsApplyedByPlaceholderOnVirtualWidget;

      this._behaviorsNameList.forEach((behaviorName) => {
        if ((!appliedByPlaceholder || !appliedByPlaceholder.has(behaviorName)) && controller._behaviorsNameList.has(behaviorName)) {
          res.add(behaviorName);
        }
      });

      return res;
    }

    /**
     * Link behavior to the controller
     * @param {Function} BehaviorClass the behavior class to link
     * @param {boolean} applyOnlyByPlaceholderWidget true if the behavior must be applied to the virtual widgets
     * @param {*} [config] configuration object
     * @protected
     */
    _addBehavior(BehaviorClass, applyOnlyByPlaceholderWidget, config) {
      super._addBehavior(BehaviorClass, config);

      if (applyOnlyByPlaceholderWidget) {
        if (!this._behaviorsApplyedByPlaceholderOnVirtualWidget) {
          this._behaviorsApplyedByPlaceholderOnVirtualWidget = new Set();
        }
        this._behaviorsApplyedByPlaceholderOnVirtualWidget.add(BehaviorClass.__name);
      }
    }

    //#endregion

    /**
     * Set the active parent id (example: Menu id)
     * @param {number} parentId
     */
    setActiveParentId(parentId) {
      const placeholderWidget = this.getWidget();
      placeholderWidget.setActiveParentId(parentId);
    }

    //#region AUTO WIDGET creation & Binding

    /**
     * Init Auto widget from current session state
     * and auto item type
     * @private
     */
    _initAutoWidgets() {
      if (this._isPrograms) {
        this._initAutoApplication();
      } else if (this._isWindows) {
        this._initAutoWindows();
      } else if (this._isActions) {
        this._initAutoActions();
      }
    }

    /**
     * Init auto widget for all applications
     * of the session
     * @private
     */
    _initAutoApplication() {
      const currentSession = context.SessionService.getCurrent();
      // We should do a getRootApplication to avoid filtering
      let applications = currentSession.getApplications();
      for (const app of applications) {
        if (!app.isAwaited()) {
          this._addApplication(app);
        }
      }
    }

    /**
     * Init auto widget for windows
     * of root waiting application
     * @private
     */
    _initAutoWindows() {
      const currentSession = context.SessionService.getCurrent();
      const currentApplication = currentSession.getCurrentApplication();
      const windows = currentSession.getNavigationManager().getWindows(this.getAnchorNode().getApplication());
      for (const windowNode of windows) {
        this._addWindow(currentApplication, windowNode);
      }
    }

    /**
     * Init auto widget for all actions
     * of the parent application current window
     * @private
     */
    _initAutoActions() {
      //The placeholder is attached now. So we can add the actions
      let uiNode = this.getAnchorNode().getApplication().uiNode();
      let windowNode = uiNode.getFirstChildWithId(uiNode.attribute("currentWindow"));

      if (!windowNode || this.isInChromeBar()) {
        return;
      }
      for (const menuActionNode of windowNode.getDescendants("MenuAction")) {
        if (menuActionNode.getController()) {
          this._addAction(menuActionNode.getController());
          menuActionNode.getController().addPlaceholderController(this);
        }
      }
      for (const actionNode of windowNode.getDescendants("Action")) {
        if (actionNode.getController()) {
          this._addAction(actionNode.getController());
        }
      }
    }

    /**
     * Add an application in the placeholder
     * @param {classes.VMApplication} application
     * @private
     */
    _addApplication(application) {
      let widget = this._createWidget(this.isInChromeBar() ? "Item" : this._autoWidgetType);

      widget.setText(application.getTitle());
      if (application.getImage()) {
        widget.setImage(application.getImage());
      }

      const titleChangeHandler = application.when(context.constants.widgetEvents.titleChanged, (evt, invoker, title) => {
        widget.setText(title);
      });
      const imageChangeHandler = application.when(context.constants.widgetEvents.iconChanged, (evt, invoker, image) => {
        widget.setImage(image);
      });
      const onClickHandler = widget.onClick((evt, invoker) => {
        application.getUI().getWidget()?.requestDisplay();
      });
      // Unregister handlers when destroying the widget
      widget.when(
        context.constants.widgetEvents.destroyed,
        () => {
          titleChangeHandler();
          imageChangeHandler();
          onClickHandler();
        },
        true
      );

      this._registerAutoWidget(application.applicationHash, widget);
    }

    /**
     * Add a window in the placeholder
     * This method don't compare this controller application
     * and windowNode application.
     * So be carefull when calling it
     * @param {classes.VMApplication} application
     * @param {classes.WindowNode} windowNode
     * @private
     */
    _addWindow(application, windowNode) {
      let widget = this._createWidget(this.isInChromeBar() ? "Item" : this._autoWidgetType);
      widget.setText(windowNode.getTitle());
      widget.setImage(windowNode.getIcon());

      widget.setEnabled(false); // Window list is disabled

      const titleChangeHandler = windowNode.when(context.constants.widgetEvents.titleChanged, (evt, invoker, title) => {
        widget.setText(title);
      });
      const imageChangeHandler = windowNode.when(context.constants.widgetEvents.iconChanged, (evt, invoker, image) => {
        widget.setImage(image);
      });
      const onClickHandler = widget.onClick(() => {
        if (windowNode.isModal()) {
          return;
        }
        gbc.SessionService.getCurrent().getNavigationManager().displayWindow(windowNode);
      });

      // Unregister handlers when destroying the widget
      widget.when(
        context.constants.widgetEvents.destroyed,
        () => {
          titleChangeHandler();
          imageChangeHandler();
          onClickHandler();
        },
        true
      );
      this._registerAutoWidget(this._generateNodeIdentifier(windowNode), widget);
    }

    /**
     * Add Menu Actions in the placeholder
     * for each action node of the windowNode
     * @param {classes.ControllerBase} actionController
     * @private
     */
    _addAction(actionController) {
      const actionNode = actionController.getAnchorNode(),
        identifier = this._generateNodeIdentifier(actionNode);

      // Use virtual widgets so the behavior keep working
      let widget = actionController.createVirtualWidget(this, this.isInChromeBar() ? "Item" : this._autoWidgetType);
      if (!widget) {
        return;
      }

      let text = actionNode.attribute("text") || actionNode.attribute("name");
      widget.setText(text);
      widget.setImage(actionNode.attribute("image"));
      widget.setAuiName(actionNode.attribute("name"));

      let onClickHandler = widget.onClick(() => {
        if (actionNode.attribute("active")) {
          const actionService = actionNode.getApplication().getActionApplicationService();
          actionService.executeAction(actionNode, { sendValue: true });
        }
      });

      let detachHandler = actionController.when(context.constants.controllerEvents.detachUi, (evt, invoker) => {
        this._destroyAutoWidget(identifier);
      });

      // Unregister handlers when destroying the widget.
      // as we don't manage the order of destruction of the controllers
      // it's possible that the action controller got destroyed before this controller.
      // Or the opposite can happen. So we need to unbind
      // at the moment the handlers are considered obsolete
      widget.when(
        context.constants.widgetEvents.destroyed,
        () => {
          onClickHandler();
          onClickHandler = null;
          if (detachHandler) {
            detachHandler();
          }
          detachHandler = null;
        },
        true
      );

      this._registerAutoWidget(identifier, widget);
    }

    /**
     * Generate an identifier to be able to query the window node afterward
     * @param {cls.WindowNode} node
     * @returns {string}
     */
    _generateNodeIdentifier(node) {
      return `${node.getApplication().applicationHash}-${node.getId()}`;
    }

    //#endregion

    //#region AUTO WIDGET management

    /**
     * Store the widget in the Widgets Map.
     * And add it to the parent widget, displaying it in the dom
     * @param {string|number} identifier The identifier to query the widget later on. The identifier will be converted into a string
     * @param {cls.WidgetBase} widget The widget to register and add to the parent
     */
    _registerAutoWidget(identifier, widget) {
      identifier = String(identifier);
      if (this._autoWidgets.has(identifier)) {
        context.LogService.warn(`Auto Widget is registered twice with the key : ${identifier}
            Skip Second Registration, and destroy the widget`);
        widget.destroy();
        return;
      }
      this._autoWidgets.set(identifier, widget);
      this.getWidget().addChildWidget(widget);
    }

    /**
     * Destroy and remove the widget from the widget Map
     * @param {string} identifier The identifier linked to the widget. See this._autoWidgets documentation
     */
    _destroyAutoWidget(identifier) {
      identifier = String(identifier);
      const widgetExists = this._autoWidgets.has(identifier);
      if (!widgetExists) {
        return;
      }
      const widget = this._autoWidgets.get(identifier);
      widget.destroy();
      this._autoWidgets.delete(identifier);
      if (this._affectLayout) {
        let layoutEngine = this.getWidget().getLayoutEngine();
        layoutEngine.invalidateMeasure();
        layoutEngine.forceMeasurement();

        layoutEngine = this.getWidget().getParentWidget().getLayoutEngine();
        layoutEngine.invalidateMeasure();
        layoutEngine.forceMeasurement();
      }
    }

    /**
     * Destroy all the auto widgets that were created
     * And clear the auto widget map
     * @private
     */
    _destroyAutoWidgets() {
      for (const [, widget] of this._autoWidgets) {
        widget.destroy();
      }
      this._autoWidgets.clear();
    }
    //#endregion
  }

  cls.ControllerPlaceholderBase = ControllerPlaceholderBase;
  ControllerPlaceholderBase.prototype.__name = "ControllerPlaceholderBase";
});
